/**
 * Collection Product Availability Sorter
 * 
 * Automatically moves out-of-stock products to the end of the collection grid
 * while maintaining the original sort order within available/unavailable groups.
 * Works across all pages and with all filter/sort combinations.
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      collectionGrid: '[data-collection-products] .grid',
      productItem: '[data-product-block]',
      promoItem: '[data-promo]',
      ajaxinateContainer: '#AjaxinateLoop'
    },
    attributes: {
      available: 'data-product-available',
      productId: 'data-product-id'
    },
    classes: {
      soldOut: 'soldout',
      reordered: 'availability-reordered'
    }
  };

  /**
   * Main class for handling product availability sorting
   */
  class AvailabilitySorter {
    constructor() {
      this.isProcessing = false;
      this.observer = null;
      this.init();
    }

    /**
     * Initialize the sorter
     */
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    /**
     * Setup the sorting functionality
     */
    setup() {
      // Initial sort
      this.sortProducts();

      // Watch for pagination/infinite scroll changes
      this.observeGridChanges();

      // Listen for filter/sort updates
      this.attachFilterListeners();
    }

    /**
     * Sort products in the grid
     */
    sortProducts() {
      if (this.isProcessing) return;
      this.isProcessing = true;

      const grid = document.querySelector(CONFIG.selectors.collectionGrid);
      
      if (!grid) {
        this.isProcessing = false;
        return;
      }

      // Check if already processed
      if (grid.classList.contains(CONFIG.classes.reordered)) {
        this.isProcessing = false;
        return;
      }

      const productItems = Array.from(grid.querySelectorAll(CONFIG.selectors.productItem));
      const promoItems = Array.from(grid.querySelectorAll(CONFIG.selectors.promoItem));
      
      if (productItems.length === 0) {
        this.isProcessing = false;
        return;
      }

      // Separate products by availability
      const availableProducts = [];
      const unavailableProducts = [];

      productItems.forEach((item) => {
        const isAvailable = item.getAttribute(CONFIG.attributes.available) === 'true';
        
        if (isAvailable) {
          availableProducts.push(item);
        } else {
          unavailableProducts.push(item);
        }
      });

      // Create a map of original positions for promos
      const promoPositions = new Map();
      promoItems.forEach((promo) => {
        const index = Array.from(grid.children).indexOf(promo);
        promoPositions.set(promo, index);
      });

      // Clear the grid
      grid.innerHTML = '';

      // Reinsert items: available products first, then unavailable
      const allSortedProducts = [...availableProducts, ...unavailableProducts];
      let productIndex = 0;

      // Calculate total slots needed
      const totalSlots = allSortedProducts.length + promoItems.length;

      for (let i = 0; i < totalSlots; i++) {
        // Check if there's a promo at this position
        let promoAtPosition = null;
        promoPositions.forEach((position, promo) => {
          if (position === i) {
            promoAtPosition = promo;
          }
        });

        if (promoAtPosition) {
          grid.appendChild(promoAtPosition);
        } else if (productIndex < allSortedProducts.length) {
          grid.appendChild(allSortedProducts[productIndex]);
          productIndex++;
        }
      }

      // Mark as processed
      grid.classList.add(CONFIG.classes.reordered);

      // Log results for debugging
      console.log(`[Availability Sorter] Sorted ${availableProducts.length} available and ${unavailableProducts.length} unavailable products`);

      this.isProcessing = false;
    }

    /**
     * Observe grid for changes (pagination, infinite scroll)
     */
    observeGridChanges() {
      const ajaxinateContainer = document.querySelector(CONFIG.selectors.ajaxinateContainer);
      
      if (!ajaxinateContainer) {
        return;
      }

      // Create mutation observer
      this.observer = new MutationObserver((mutations) => {
        let shouldResort = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if new product items were added
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node.matches(CONFIG.selectors.productItem)) {
                shouldResort = true;
              }
            });
          }
        });

        if (shouldResort) {
          // Remove reordered class to allow re-sorting
          const grid = document.querySelector(CONFIG.selectors.collectionGrid);
          if (grid) {
            grid.classList.remove(CONFIG.classes.reordered);
          }
          
          // Delay to ensure DOM is fully updated
          setTimeout(() => this.sortProducts(), 100);
        }
      });

      // Start observing
      this.observer.observe(ajaxinateContainer, {
        childList: true,
        subtree: true
      });
    }

    /**
     * Attach listeners to filter/sort controls
     */
    attachFilterListeners() {
      const collectionForm = document.querySelector('[data-collection-filters-form]');
      
      if (collectionForm) {
        // Listen for form changes (filters)
        collectionForm.addEventListener('change', () => {
          setTimeout(() => {
            const grid = document.querySelector(CONFIG.selectors.collectionGrid);
            if (grid) {
              grid.classList.remove(CONFIG.classes.reordered);
            }
            this.sortProducts();
          }, 500);
        });
      }

      // Listen for sort changes
      const sortInputs = document.querySelectorAll('[data-input-sort]');
      sortInputs.forEach((input) => {
        input.addEventListener('change', () => {
          setTimeout(() => {
            const grid = document.querySelector(CONFIG.selectors.collectionGrid);
            if (grid) {
              grid.classList.remove(CONFIG.classes.reordered);
            }
            this.sortProducts();
          }, 500);
        });
      });

      // Listen for tag filter clicks
      const tagButtons = document.querySelectorAll('[data-collection-filter-tag-button]');
      tagButtons.forEach((button) => {
        button.addEventListener('click', () => {
          setTimeout(() => {
            const grid = document.querySelector(CONFIG.selectors.collectionGrid);
            if (grid) {
              grid.classList.remove(CONFIG.classes.reordered);
            }
            this.sortProducts();
          }, 500);
        });
      });
    }

    /**
     * Destroy the sorter (cleanup)
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  // Initialize when the script loads
  const sorter = new AvailabilitySorter();

  // Expose to window for manual control if needed
  window.availabilitySorter = sorter;

  // Re-sort on Shopify section re-render
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => sorter.sortProducts(), 300);
  });

})();

